// pages/sections/groups/ClientGroupsSection.js
// Partner-facing Client Groups section — the multi-step DQ review workflow.
//
// Displays the current period's DQ issues scoped to the signed-in partner.
// Admins/steering can toggle to firmwide scope. Each issue is shown inside
// a collapsible issue_code group, with an expandable detail row per issue.
// Inline "Resolve" actions (confirm SG, resolve disagreement, split mixed
// group, confirm/move RG, etc.) write through the validation engine in
// resolutionVerbs.js and append audit rows.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../../auth/AuthContext';
import { usePeriod } from '../../../hooks/usePeriod';
import { useTourDemo } from '../../../lib/tourDemo';
import { CLIENT_GROUPING_DEMO } from './clientGroupingDemoData';
import DataQualityPanel, { phaseFor } from './DataQualityPanel';
import { setActionPeriod } from './resolveActionQueue';
import { primeAnchors } from './clientRosterCache';
import { loadStaffNames } from '../../../auth/staffNamesCache';
import { emitToast, dismissToast, errorText } from './toast';
import ToastViewport from './ToastViewport';
import MigrationPromptHost from './MigrationPromptHost';
import { rememberClientNames } from './ClientChip';
import './ClientGroups.css';

const CLIENT_CODE_RE = /^\d{5}-\d{3}$/;

// Client-side test-mode state. When ON, every Resolve action POST carries
// testMode:true plus a sessionId; the server captures prior state for
// reversal. Sessions persist across page reloads via localStorage so a
// partner can refresh mid-test without losing the undo log.
const TEST_MODE_KEY   = 'cg.testMode';
const TEST_SESSION_KEY = 'cg.testSessionId';
// sessionStorage keys for expansion state — keyed only by scope so the
// state survives refetches (post-Resolve) and even page refreshes during
// the same browser session.
const EXPAND_CODE_KEY = (scope) => `cg.openCode.${scope}`;
const EXPAND_ISSUE_KEY = (scope) => `cg.openIssue.${scope}`;
function readSession(key) {
  if (typeof window === 'undefined') return null;
  try { return window.sessionStorage.getItem(key); } catch { return null; }
}
function writeSession(key, value) {
  if (typeof window === 'undefined') return;
  try {
    if (value === null || value === undefined) window.sessionStorage.removeItem(key);
    else window.sessionStorage.setItem(key, String(value));
  } catch { /* sessionStorage may be unavailable in some iframes */ }
}
function newSessionId(email) {
  const stamp = new Date().toISOString().replace(/[:.]/g, '-');
  const rand  = Math.random().toString(36).slice(2, 8);
  return `test-${stamp}-${(email || 'anon').split('@')[0]}-${rand}`;
}

export default function ClientGroupsSection() {
  const { email, isAdmin, isSteering, ready, configError } = useAuth();
  const { period, periodActive } = usePeriod();
  const canSeeAll = isAdmin || isSteering;

  // Spotlight tour demo mode (flipped on by SpotlightTour while it runs). When
  // true the section serves curated fixtures instead of the real /api/dgGroupsDQ
  // feed, and all write paths no-op (see resolveActionQueue / PhaseSignoffBar).
  const demo = useTourDemo();

  const [scope,   setScope]   = useState('mine');   // 'mine' | 'all'
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [err,     setErr]     = useState(null);

  // Expansion state is owned here so a Resolve-driven refetch (or even an
  // accidental DataQualityPanel remount) doesn't collapse what the partner
  // had open. Also mirrored to sessionStorage so a hard refresh restores it.
  const [openCode, setOpenCodeRaw] = useState(() => readSession(EXPAND_CODE_KEY('mine')));
  // Holds a STABLE issue signature (issueSig), not the numeric row id —
  // ids regenerate on every Resolve-driven refetch, so a signature is what
  // lets an expanded row / open inline modal survive the refetch.
  const [expandedIssueId, setExpandedIssueIdRaw] = useState(
    () => readSession(EXPAND_ISSUE_KEY('mine')) || null
  );
  const setOpenCode = useCallback((next) => {
    setOpenCodeRaw(next);
    writeSession(EXPAND_CODE_KEY(scope), next);
  }, [scope]);
  const setExpandedIssueId = useCallback((next) => {
    setExpandedIssueIdRaw(next);
    writeSession(EXPAND_ISSUE_KEY(scope), next);
  }, [scope]);
  // When the partner flips scope, reload expansion state for that scope.
  useEffect(() => {
    setOpenCodeRaw(readSession(EXPAND_CODE_KEY(scope)));
    setExpandedIssueIdRaw(readSession(EXPAND_ISSUE_KEY(scope)) || null);
  }, [scope]);

  // Test mode: read initial state from localStorage so refreshes don't
  // lose track of in-progress test sessions. Admin-only (the server refuses test-mode writes
  // from anyone else, and only admins get the toggle): a stored flag is honoured for real
  // admins and cleared for everyone else, so a stale cg.testMode=1 can't turn every write
  // into a 403 with no way to switch it off.
  const [testModeStored, setTestMode] = useState(
    () => typeof window !== 'undefined' && window.localStorage.getItem(TEST_MODE_KEY) === '1'
  );
  const testMode = testModeStored && isAdmin;
  useEffect(() => {
    if (!ready || configError || isAdmin) return;   // a failed config load also reads isAdmin=false
    setTestMode(false);
    try { window.localStorage.removeItem(TEST_MODE_KEY); } catch { /* storage unavailable */ }
  }, [ready, configError, isAdmin]);
  const [testSessionId, setTestSessionId] = useState(
    () => typeof window !== 'undefined' ? window.localStorage.getItem(TEST_SESSION_KEY) : null
  );
  const [pendingTestCount, setPendingTestCount] = useState(0);
  const [undoBusy, setUndoBusy] = useState(false);

  // Admin-only: re-pull PE/Knuula/Snowflake source data for ONE client.
  const [refreshCode, setRefreshCode] = useState('');
  const [refreshBusy, setRefreshBusy] = useState(false);

  function enableTestMode() {
    const sid = testSessionId || newSessionId(email);
    setTestSessionId(sid);
    setTestMode(true);
    window.localStorage.setItem(TEST_MODE_KEY, '1');
    window.localStorage.setItem(TEST_SESSION_KEY, sid);
  }
  function disableTestMode() {
    setTestMode(false);
    window.localStorage.setItem(TEST_MODE_KEY, '0');
    // Keep sessionId — partner may turn test mode back on and want to
    // continue the same undo log. Reset only happens on Undo-all.
  }

  // Poll pending test actions count whenever test mode is on or the
  // panel reloads after a Resolve.
  const refreshPendingCount = useCallback(async () => {
    if (!testMode) { setPendingTestCount(0); return; }
    try {
      const r = await fetch('/api/diTestActions', { credentials: 'include' });
      if (!r.ok) return;
      const j = await r.json();
      setPendingTestCount(j.count || 0);
    } catch { /* ignore — non-blocking */ }
  }, [testMode]);

  useEffect(() => { refreshPendingCount(); }, [refreshPendingCount]);

  // Warm the SG/RG anchor cache as soon as the section mounts so the
  // SplitMemberButton / MoveToRgButton pickers open instantly. Also warm
  // the staff-names roster so partner emails render as real names without a
  // flash (useNameFor reads the same cache).
  useEffect(() => {
    primeAnchors();
    loadStaffNames().catch(() => { /* names fall back to emails */ });
  }, []);

  // Stamp every diResolveAction body with this section's period (route-scoped),
  // since client-level verbs can no longer infer it server-side.
  useEffect(() => { setActionPeriod(period); }, [period]);

  async function undoAllTestActions() {
    if (demo) return;   // tour demo mode — never hit the real undo endpoint
    if (!window.confirm(`Undo ${pendingTestCount} pending test action${pendingTestCount === 1 ? '' : 's'} across ALL your sessions? This restores affected rows to their pre-test state.`)) {
      return;
    }
    setUndoBusy(true);
    try {
      // Omit sessionId so the server reverses EVERY pending action for this
      // user, regardless of which session created it. The pending count shown
      // is already all-session (listPending isn't session-scoped), so this
      // makes the button match what it promises — and clears orphaned actions
      // left over from earlier test sessions.
      const r = await fetch('/api/diTestActions/undo', {
        method:      'POST',
        headers:     { 'Content-Type': 'application/json' },
        credentials: 'include',
        body:        JSON.stringify({ period }),
      });
      if (!r.ok) {
        const t = await r.text();
        throw new Error(t || `HTTP ${r.status}`);
      }
      const j = await r.json();
      const message = j.errors && j.errors.length
        ? `Undone: ${j.undone.length}. Errors: ${j.errors.length}. See console for details.`
        : `Undone ${j.undone.length} action${j.undone.length === 1 ? '' : 's'}.`;
      if (j.errors && j.errors.length) {
        // eslint-disable-next-line no-console
        console.warn('[Test undo] errors:', j.errors);
      }
      // Reset sessionId so the next test run starts fresh.
      const newSid = newSessionId(email);
      setTestSessionId(newSid);
      window.localStorage.setItem(TEST_SESSION_KEY, newSid);
      setPendingTestCount(0);
      load({ silent: true });
      // eslint-disable-next-line no-alert
      window.alert(message);
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert('Undo failed: ' + e.message);
    } finally {
      setUndoBusy(false);
    }
  }

  async function refreshClientSource() {
    if (refreshBusy) return;
    const code = refreshCode.trim();
    if (!CLIENT_CODE_RE.test(code)) {
      emitToast({ kind: 'error', message: 'Enter a client code like 00069-001.' });
      return;
    }
    setRefreshBusy(true);
    const progressId = emitToast({
      kind: 'info',
      message: `Refreshing source data for ${code}…`,
      durationMs: 0,
    });
    try {
      const r = await fetch('/api/dgRefreshClientSource', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientCode: code }),
      });
      if (!r.ok) throw new Error((await r.text()) || `HTTP ${r.status}`);
      const j = await r.json();
      dismissToast(progressId);
      if (j.foundInPe === false) {
        emitToast({ kind: 'warn', message: `${code} isn't in the PE feed — nothing changed.` });
      } else {
        emitToast({ kind: 'success', message: `Refreshed source data for ${code}.` });
        setRefreshCode('');
        await load({ silent: true });
      }
    } catch (e) {
      dismissToast(progressId);
      emitToast({ kind: 'error', message: `Refresh failed: ${errorText(e)}` });
    } finally {
      setRefreshBusy(false);
    }
  }

  // Latest request wins: rapid optimistic actions each trigger a quiet refetch, and
  // an older response must never overwrite a newer one.
  const reqId = useRef(0);
  // `silent` = a background reconcile after an optimistic action / heartbeat-style
  // refresh: it never flips the page to "Loading…" and a failure keeps the data
  // already on screen (toast only) instead of blanking the page with an error.
  const load = useCallback(async ({ silent = false } = {}) => {
    if (!period) return;
    // Tour demo: serve the curated fixtures and skip the real fetch entirely.
    // Re-runs when `demo` flips off → the real data refetches on tour close.
    if (demo) {
      setData(CLIENT_GROUPING_DEMO);
      setErr(null);
      setLoading(false);
      return;
    }
    const mine = ++reqId.current;
    if (!silent) {
      setLoading(true);
      setErr(null);
    }
    try {
      const qs = scope === 'all' && canSeeAll ? '?scope=all' : '';
      const res = await fetch(`/api/dgGroupsDQ/${period}${qs}`, { credentials: 'include' });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(errorText({ message: text || `HTTP ${res.status}` }));
      }
      const json = await res.json();
      if (mine !== reqId.current) return;
      rememberClientNames(json.issues);   // so a chip anywhere on the page can show a client's name next to its code
      setData(json);
      setErr(null);
    } catch (e) {
      if (mine !== reqId.current) return;
      if (silent) {
        emitToast({ kind: 'warn', message: `Couldn't refresh Client Groups: ${e.message}. Showing the last loaded data.`, durationMs: 10000 });
      } else {
        setErr(e.message);
        setData(null);
      }
    } finally {
      if (mine === reqId.current) setLoading(false);
    }
  }, [period, scope, canSeeAll, demo]);

  useEffect(() => { load(); }, [load]);

  const initialLoading = loading && !data;
  if (initialLoading) {
    return <div className="cg-section"><p>Loading Client Groups…</p></div>;
  }

  // Gate the error screen on `!data` so a failed background refetch never blanks the page.
  if (err && !data) {
    return (
      <div className="cg-section">
        <h2 className="cg-section-title">Client Groups</h2>
        <div className="cg-error">Error loading issues: {err}</div>
        <button className="cg-btn" onClick={() => load()}>Retry</button>
      </div>
    );
  }

  const { breakdown, openBreakdown, issues, signoffs: serverSignoffs, pending, rgMemberships, truncated, earlyStandalone } = data || {};
  // A step with nothing left to review needs no sign-off: every client in it was signed off in an
  // earlier cycle (and no issue is open in it), so it reads as `auto` — complete — and unlocks the
  // next step. `pending` is the caller's unreviewed-client count per layer; a server sign-off
  // is already nulled while its layer has unreviewed clients. Without `pending` (tour demo,
  // older API) we take the server's word.
  const signoffs = (() => {
    const base = serverSignoffs || { sg: null, rg: null, standalone: null };
    if (!pending) return base;
    const full = [0, 0, 0, 0, 0];
    for (const g of (breakdown || [])) full[phaseFor(g.issueCode)] += g.count;
    const auto = { auto: true };
    return {
      sg:         base.sg         || (pending.sg === 0 && full[1] === 0 && full[2] === 0 ? auto : null),
      rg:         base.rg         || (pending.rg === 0 && full[3] === 0 && !(rgMemberships || []).length ? auto : null),
      standalone: base.standalone || (pending.standalone === 0 && full[4] === 0 ? auto : null),
    };
  })();
  // Summary cards reflect OUTSTANDING work: confirmed / signed-off
  // RG_NEEDS_CONFIRMATION rows linger as open DQ rows but need no further action,
  // so they shouldn't inflate "Open issues" / "Need Confirmations". openBreakdown
  // already excludes them; fall back to the full breakdown for older API responses.
  const severitySource = openBreakdown || breakdown || [];
  // "Need Confirmations" = every confirmation the partner still owes, whatever its severity:
  // WARN rows plus the INFO-severity review rows — a Relationship Group to confirm (Step 4)
  // and a standalone individual to confirm (Step 5) — plus the member-partner RG agreements
  // still open. Counting WARN alone read 0 with thousands of these outstanding.
  const CONFIRMATION_CODES = ['SG_NEEDS_CONFIRMATION', 'RG_NEEDS_CONFIRMATION', 'STANDALONE_INDIVIDUAL_NEEDS_REVIEW'];
  const warns  = severitySource
    .filter(r => r.severity === 'WARN' || CONFIRMATION_CODES.includes(r.issueCode))
    .reduce((a, r) => a + r.count, 0)
    + (rgMemberships || []).filter(m => !m.confirmed).length;

  // "Open issues" = only what's actionable in the partner's CURRENT step. The
  // workflow gates each phase (Step 1 must clear before 2; 3/4 need the prior
  // sign-off), so we walk the same gate chain DataQualityPanel uses and return
  // the first unlocked phase's OUTSTANDING count (openBreakdown excludes
  // already-confirmed RG rows). Step 3 (SG sign-off) and Step 5 (after RG
  // sign-off) have no issues to resolve, so they read 0.
  const currentStepOpen = (() => {
    const out  = [0, 0, 0, 0, 0];   // outstanding by phase
    const full = [0, 0, 0, 0, 0];   // full by phase (drives the same gate as the panel)
    for (const g of (openBreakdown || [])) out[phaseFor(g.issueCode)]  += g.count;
    for (const g of (breakdown || []))     full[phaseFor(g.issueCode)] += g.count;
    if (full[1] > 0) return out[1];                 // Step 1 — resolve invalid data
    if (full[2] > 0) return out[2];                 // Step 2 — confirm signer groups
    if (!signoffs?.sg) return 0;                    // Step 3 — sign off SGs (no issues)
    const rgOpen = out[3] + (rgMemberships || []).filter(m => !m.confirmed).length;
    if (rgOpen > 0) return rgOpen;                  // Step 4 — relationship groups
    if (!signoffs?.rg) return 0;                    // sign off RGs (no issues)
    return out[4];                                  // Step 5 — standalone individuals
  })();

  return (
    <div className="cg-section">
      <div className="cg-section-header">
        <div>
          <h2 className="cg-section-title">Client Groups</h2>
          <p className="cg-section-sub">
            {scope === 'mine'
              ? <>Review the signer groups and relationship groups of clients where you are the partner. Only clients that haven't been signed off in an earlier cycle need your sign-off — you can still view and change any of your groupings.</>
              : <>Firmwide data-quality issues across all partners.</>}
          </p>
        </div>

        <div className="cg-header-controls">
          {/* Admin-only: re-pull PE + live Knuula source data for one
              client without touching any other client's data. Busts the PE
              cache, re-stages just this code, re-materializes + revalidates. */}
          {isAdmin && (
            <form
              className="cg-refresh-src"
              onSubmit={(e) => { e.preventDefault(); refreshClientSource(); }}
              title="Re-pull source data (PE / Knuula) for a single client"
            >
              <input
                className="cg-refresh-input"
                placeholder="Client code…"
                value={refreshCode}
                onChange={(e) => setRefreshCode(e.target.value)}
                disabled={refreshBusy}
                aria-label="Client code to refresh source data for"
              />
              <button
                type="submit"
                className="cg-btn cg-btn--secondary"
                disabled={refreshBusy || !refreshCode.trim()}
              >
                {refreshBusy ? 'Refreshing…' : 'Refresh source'}
              </button>
            </form>
          )}

          {/* Test mode toggle — admins ONLY. It writes to the real DB
              (reversible via Undo below), so steering (read-only) doesn't
              get it even though they can see the Firmwide view. */}
          {isAdmin && (
            <div className="cg-test-toggle">
              <label>
                <input
                  type="checkbox"
                  checked={testMode}
                  onChange={(e) => e.target.checked ? enableTestMode() : disableTestMode()}
                />
                <span>🧪 Test mode</span>
              </label>
            </div>
          )}

          {canSeeAll && (
            <div className="cg-scope-toggle" data-tour="cg-scope">
              <label>View:</label>
              <button
                className={`cg-scope-btn ${scope === 'mine' ? 'active' : ''}`}
                onClick={() => setScope('mine')}
              >
                My clients
              </button>
              <button
                className={`cg-scope-btn ${scope === 'all' ? 'active' : ''}`}
                onClick={() => setScope('all')}
              >
                Firmwide
              </button>
            </div>
          )}
        </div>
      </div>

      {testMode && (
        <div className="cg-test-banner">
          <span className="cg-test-banner-icon" aria-hidden="true">🧪</span>
          <span className="cg-test-banner-text">
            <strong>Test mode is ON.</strong> Resolve actions will write to the database
            and are <em>reversible</em>. Session:{' '}
            <code>{testSessionId || '(initializing…)'}</code>
          </span>
          <button
            type="button"
            className="cg-btn cg-btn--danger"
            disabled={undoBusy || pendingTestCount === 0}
            onClick={undoAllTestActions}
          >
            {undoBusy
              ? 'Undoing…'
              : pendingTestCount > 0
                ? `Undo ${pendingTestCount} test action${pendingTestCount === 1 ? '' : 's'}`
                : 'No test actions yet'}
          </button>
        </div>
      )}

      <div className="cg-summary-row">
        <div className="cg-summary-card" title="Issues you can act on in your current step. Earlier steps must be cleared (and signed off) before later steps unlock.">
          <div className="cg-summary-label">Open issues</div>
          <div className="cg-summary-value">{currentStepOpen.toLocaleString()}</div>
        </div>
        <div className="cg-summary-card cg-summary-warn" title="Signer Groups, Relationship Groups and standalone individuals still waiting for your confirmation (all steps, not just the current one).">
          <div className="cg-summary-label">Need Confirmations</div>
          <div className="cg-summary-value">{warns.toLocaleString()}</div>
        </div>
        <div className="cg-summary-card">
          <div className="cg-summary-label">Period</div>
          <div className="cg-summary-value cg-summary-period">{period || '—'}</div>
        </div>
      </div>

      {truncated && (
        <div className="cg-notice cg-notice-warn">
          Showing the first 2,000 issues. Filter down in the Admin panel or
          contact the data-integrity team if your partner scope is this large.
        </div>
      )}

      {!periodActive && !demo && (
        <div className="cg-period-closed-banner" role="status">
          <i className="fas fa-lock" aria-hidden="true" />{' '}
          <strong>This period is closed.</strong> Resolve actions and step
          attestations are locked. The panel below stays available for
          read-only review; any submitted action will be rejected by the API.
        </div>
      )}

      <DataQualityPanel
        breakdown={breakdown || []}
        issues={issues || []}
        email={email}
        isAdmin={isAdmin}
        scope={scope}
        period={period}
        demo={demo}
        earlyStandalone={Boolean(earlyStandalone)}
        signoffs={signoffs || { sg: null, rg: null, standalone: null }}
        rgMemberships={rgMemberships || []}
        testMode={testMode}
        testSessionId={testSessionId}
        openCode={openCode}
        setOpenCode={setOpenCode}
        expandedIssueId={expandedIssueId}
        setExpandedIssueId={setExpandedIssueId}
        onResolved={async () => { await load({ silent: true }); refreshPendingCount(); }}
      />
      <ToastViewport />
      <MigrationPromptHost />
    </div>
  );
}
